import { z } from 'zod';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  normalizeStringArray,
  isSubscriptionBlocked,
  type SubscriptionStatus,
} from '@/lib/billing';
import { isModulePathAccessible } from '@/lib/module-access';
import { computeEnabledModulesFromTenantRow } from '@/lib/supabase/tenant-modules-compute';
export const COMPLIANCE_REGISTER_REVISION = 'COMPLIANCE_REGISTER_V1';
export const COMPLIANCE_REGISTER_ROUTES = [
  'CARE_INCIDENT_CAPTURE',
  'WHS_INCIDENT_CAPTURE',
] as const;
export type ComplianceRegisterRoute = (typeof COMPLIANCE_REGISTER_ROUTES)[number];
export const complianceRegisterTenantSchema = z
  .object({
    billing_plan: z.string().nullable(),
    brand_config: z.record(z.string(), z.unknown()).nullable(),
    selected_packages: z.array(z.string()).nullable(),
    is_demo: z.boolean().nullable(),
  })
  .strict();
const paths = {
  CARE_INCIDENT_CAPTURE: '/compliance/incidents/new',
  WHS_INCIDENT_CAPTURE: '/compliance/whs/incidents/new',
} as const;

/** Shared tenant evaluator for publication and web admission. No subscription-derived modules. */
export function evaluateComplianceRegisterTenant(
  tenant: z.infer<typeof complianceRegisterTenantSchema>,
  route: ComplianceRegisterRoute,
) {
  const brand = tenant.brand_config ?? {};
  // Unknown/missing configuration is not an explicit disable. Existing module
  // computation resolves known plans/packages and explicit administrator choices.
  const configured = Boolean(
    tenant.billing_plan ||
    tenant.selected_packages?.length ||
    brand.plan ||
    brand.selectedPackages ||
    brand.enabledModules ||
    brand.adminDisabledModules,
  );
  const modules = configured
    ? computeEnabledModulesFromTenantRow(
        { ...tenant, subscription_tier: null, subscription_status: null },
        [],
      )
    : null;
  const forced = normalizeStringArray(brand.systemEnabledModules);
  const disabled = normalizeStringArray(brand.adminDisabledModules).filter(
    (path) => !forced.includes(path),
  );
  // A disabled register module covers its descendant paths. Otherwise a
  // remaining explicit child grant can accidentally keep its capture enabled.
  const explicitlyDisabled = disabled.some(
    (path) => paths[route] === path || paths[route].startsWith(`${path}/`),
  );
  return (
    !explicitlyDisabled &&
    (modules === null ||
      isModulePathAccessible(paths[route], modules, { isDemoTenant: tenant.is_demo === true }))
  );
}

export function hasAccessibleComplianceRegister(
  tenant: z.infer<typeof complianceRegisterTenantSchema>,
) {
  return COMPLIANCE_REGISTER_ROUTES.some((route) =>
    evaluateComplianceRegisterTenant(tenant, route),
  );
}
/** Both server layout and client gate use tenant-scoped reads and the publisher's evaluator. */
export async function readUnifiedCaptureModuleAdmission(client: SupabaseClient, tenantId: string) {
  try {
    const { data, error } = await client
      .from('tenants')
      .select('billing_plan, brand_config, selected_packages, is_demo, subscription_status')
      .eq('id', tenantId)
      .maybeSingle();
    if (error || !data || isSubscriptionBlocked(data.subscription_status as SubscriptionStatus))
      return false;
    const { subscription_status: _subscription, ...tenant } = data;
    return hasAccessibleComplianceRegister(complianceRegisterTenantSchema.parse(tenant));
  } catch {
    return false;
  }
}
